import type { ReactNode } from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import { CHEVRON_LEFT, Glyph, PERSON, TICK } from '~/components/decision';
import type { Palette } from '~/theme';
import { useTheme } from '~/theme/ThemeProvider';

/**
 * Shared primitives for the redesigned flow.
 *
 * Built once because the handoff reuses the same handful across all eight screens, and
 * because the rules that matter — 44pt minimum targets, 58pt CTAs, disabled state
 * signalled by FILL rather than opacity — are easy to get subtly wrong per screen.
 *
 * Styles are built per render from the theme rather than via StyleSheet.create at
 * module scope: the palette is a runtime value, so a module-scope stylesheet would
 * freeze whichever theme happened to be active on first import.
 */

/**
 * Small uppercase label. Section headings, step counters, metadata.
 *
 * 11/600 with wide tracking, and it is the ONE place the type ramp goes below 14 — an
 * eyebrow is scanned for position, not read as a sentence.
 */
export function Eyebrow({
  children,
  tone = 'dim2',
}: {
  children: ReactNode;
  tone?: 'dim2' | 'dim' | 'primary' | 'brandPrimary' | 'noticeCaveat' | 'onInverseMuted';
}) {
  const { c, t } = useTheme();
  return (
    <Text style={[t.monoEyebrow, { color: c[tone], textTransform: 'uppercase' }]}>{children}</Text>
  );
}

export function Card({
  children,
  style,
  tone,
}: {
  children: ReactNode;
  style?: object;
  /** Border colour override — used to flag a card that needs attention. */
  tone?: string;
}) {
  const { c, radius, space } = useTheme();
  return (
    <View
      style={[
        {
          backgroundColor: c.s2,
          borderRadius: radius.lg,
          borderWidth: 1,
          borderColor: tone ?? c.border,
          padding: space.md,
        },
        style,
      ]}
    >
      {children}
    </View>
  );
}

/**
 * What every button here takes.
 *
 * Split into three shapes below rather than one shared interface. One interface promised
 * every button could do everything: `busy` and `sub` compiled on an OutlineButton and did
 * nothing, and `onDark` compiled on a PrimaryButton that ignored it — which is how a CTA
 * ended up drawn in ink on the ink-coloured camera screens without the type system
 * noticing. The union is the honest description of what exists.
 */
interface BaseButtonProps {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  testID?: string;
  /**
   * Draw for the inverse surface — the camera screens, which are the one dark place in
   * this app. Not a second theme: the same button with the ink/white pair swapped, so a
   * viewfinder control does not need its own component to stay legible on black.
   */
  onDark?: boolean;
}

interface PrimaryButtonProps extends BaseButtonProps {
  busy?: boolean;
  /** Secondary line under the label, e.g. what is still missing. */
  sub?: string;
  /**
   * 16-grid stroke path, drawn before the label.
   *
   * A real glyph rather than a "+ " prefix in the label text: the prefix ends up in the
   * accessible name, so a screen reader announced the button as "plus New inspection".
   */
  glyph?: string;
}

type ButtonProps = BaseButtonProps;

/**
 * Primary call to action. 58pt per the handoff.
 *
 * Disabled state is a FILL and INK change, never reduced opacity — opacity fails in
 * direct sunlight, which is where this app is used.
 */
/** The plus on "New inspection", from the design system's 16-grid set. */
export const PLUS = 'M8 3v10M3 8h10';

export function PrimaryButton({
  label,
  onPress,
  disabled,
  busy,
  sub,
  testID,
  onDark,
  glyph,
}: PrimaryButtonProps) {
  const { c, t, radius, space, touch } = useTheme();
  const off = disabled || busy;
  /*
    On the inverse surface the fill and the ink swap. They have to: `primary` and `inverse`
    are the SAME hex — the design system's CTA is neutral-950 and so is the camera
    background — so an unswapped PrimaryButton on a viewfinder is an ink rectangle on ink,
    readable only by its white label and with no visible edge at all.
  */
  const fill = onDark ? c.surface : c.primary;
  const ink = onDark ? c.text : c.onPrimary;
  const offFill = onDark ? c.inverseRaise : c.s3;
  const offInk = onDark ? c.onInverseMuted : c.dim;
  const offRule = onDark ? c.inverseRule : c.border;
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityState={{ disabled: !!off }}
      disabled={off}
      onPress={onPress}
      style={{
        minHeight: touch.cta,
        borderRadius: radius.lg,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 16,
        backgroundColor: off ? offFill : fill,
        borderWidth: off ? 1 : 0,
        borderColor: offRule,
      }}
    >
      {busy ? (
        <ActivityIndicator color={off ? offInk : ink} />
      ) : (
        <>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.sm }}>
            {glyph ? <Glyph path={glyph} color={off ? offInk : ink} /> : null}
            <Text style={[t.bodyLg, { fontWeight: '600', color: off ? offInk : ink }]}>
              {label}
            </Text>
          </View>
          {sub ? <Text style={[t.micro, { color: off ? offInk : ink }]}>{sub}</Text> : null}
        </>
      )}
    </Pressable>
  );
}

export function OutlineButton({ label, onPress, disabled, testID, onDark }: ButtonProps) {
  const { c, t, radius, space, touch } = useTheme();
  const ink = onDark ? c.onInverse : c.text;
  const rule = onDark ? c.inverseRule : c.border2;
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityState={{ disabled: !!disabled }}
      disabled={disabled}
      onPress={onPress}
      style={{
        minHeight: touch.min,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: disabled ? c.border : rule,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: space.md,
      }}
    >
      <Text style={[t.label, { color: disabled ? (onDark ? c.onInverseMuted : c.dim) : ink }]}>
        {label}
      </Text>
    </Pressable>
  );
}

/**
 * Flat text action. Still 44pt tall — a text link is not an excuse for a small target.
 *
 * 14/600, not the old 12: the design system's floor for anything read at arm's length is
 * 14, and these are real exits (skip the VIN, scan again), not captions.
 */
export function TextButton({ label, onPress, testID, onDark }: ButtonProps) {
  const { c, t, space, touch } = useTheme();
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      onPress={onPress}
      style={{
        minHeight: touch.min,
        paddingHorizontal: space.sm,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Text style={[t.label, { color: onDark ? c.onInverse : c.text }]}>{label}</Text>
    </Pressable>
  );
}

/**
 * The frame's screen header: a chevron that goes back, a title, and an optional right slot.
 *
 * One component because four screens draw it and the 44 pt rule is the part that goes
 * wrong per screen — the chevron's own glyph box is ~14 pt, so hit-slop alone leaves the
 * VISIBLE target and the touched target disagreeing about where the button is.
 */
export function BackHeader({
  title,
  onBack,
  right,
  backLabel = 'Back',
}: {
  title: string;
  onBack: () => void;
  right?: ReactNode;
  /** What the button announces. The title is where it goes, not what it does. */
  backLabel?: string;
}) {
  const { c, t, space, touch } = useTheme();
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: space.sm,
        paddingHorizontal: space.md,
      }}
    >
      <Pressable
        onPress={onBack}
        accessibilityRole="button"
        accessibilityLabel={backLabel}
        testID="back"
        hitSlop={12}
        style={{
          width: touch.min,
          height: touch.min,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Glyph path={CHEVRON_LEFT} size={20} color={c.text} />
      </Pressable>
      <Text style={[t.bodyLg, { fontWeight: '600', color: c.text, flex: 1 }]}>{title}</Text>
      {right}
    </View>
  );
}

/** Caution triangle and critical circle, from the design system's 16-grid icon set. */
const TRIANGLE = 'M8 2l6.5 11.5h-13zM8 6.5v3M8 11.5v.2';
const EXCLAIM = 'M8 13.5a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11zM8 5v3.5M8 10.8v.2';
/** Lower-case i in a circle, for an informational notice that is not about review. */
export const INFO = 'M8 2.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11zM8 7.5v3.5M8 5.2v.2';

type NoticeTone = 'noticeCaveat' | 'stateFailed' | 'stateInfo' | 'decisionRepair';

const NOTICE: Record<NoticeTone, { fg: keyof Palette; bg: keyof Palette; glyph: string }> = {
  noticeCaveat: { fg: 'noticeCaveat', bg: 'noticeCaveatBg', glyph: TRIANGLE },
  stateFailed: { fg: 'stateFailed', bg: 'stateFailedBg', glyph: EXCLAIM },
  stateInfo: { fg: 'stateInfo', bg: 'stateInfoBg', glyph: PERSON },
  decisionRepair: { fg: 'decisionRepair', bg: 'decisionRepairBg', glyph: TICK },
};

/**
 * A notice that is cautionary, a failure, informational or a confirmation.
 *
 * The fill is a NAMED design-system surface, not the tone colour at 12% alpha. The old
 * derived tint was a guess that happened to look right on one background; the system ships
 * a measured pair per tone, and an alpha fill over a photo or a camera view is not the
 * same colour twice.
 *
 * The glyph is not decoration. Every notice here is read outdoors where the fill itself is
 * washed out, so the shape carries the severity when the colour cannot, and `title` puts
 * the same severity into words.
 *
 * `glyph` overrides the tone's default. The info tone's default is the person mark, which
 * means "a reviewer has this" everywhere else in both consoles — so anything else using
 * that tone has to say what it actually is.
 */
export function Notice({
  children,
  title,
  tone = 'noticeCaveat',
  glyph,
  testID,
}: {
  children: ReactNode;
  /** Leading sentence, set in the tone colour. The detail after it stays ink. */
  title?: string;
  tone?: NoticeTone;
  /** 16-grid stroke path, when the tone's default mark would mislead. */
  glyph?: string;
  /** For a notice a test needs to assert the ABSENCE of — a failure state, usually. */
  testID?: string;
}) {
  const { c, t, radius, space } = useTheme();
  const { fg, bg, glyph: defaultGlyph } = NOTICE[tone];
  const mark = glyph ?? defaultGlyph;
  return (
    <View
      testID={testID}
      style={{
        backgroundColor: c[bg],
        borderRadius: radius.md,
        padding: space.sm,
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: space.sm,
      }}
    >
      <View style={{ paddingTop: 2 }}>
        <Glyph path={mark} color={c[fg]} />
      </View>
      <Text style={[t.body, { color: c.text, flex: 1, lineHeight: 20 }]}>
        {title ? <Text style={{ fontWeight: '600', color: c[fg] }}>{`${title} `}</Text> : null}
        {children}
      </Text>
    </View>
  );
}
